import axios from 'axios'
import config from '@/config'
import { ElMessage } from 'element-plus'

// 创建 axios 实例
const service = axios.create({
  baseURL: config.baseURL,
  timeout: config.timeout,
  withCredentials: false, // 禁用携带跨域 cookies，避免CORS错误
})

// 工具：将普通对象递归转换为 FormData（数组用 key[] 重复追加，嵌套对象用 bracket 写法）
function buildFormData(formData, data, parentKey) {
  if (data === undefined || data === null) return
  const isDate = data instanceof Date
  const isFile = (typeof File !== 'undefined' && data instanceof File) || (typeof Blob !== 'undefined' && data instanceof Blob)
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData

  if (isFormData) {
    // 已经是 FormData，直接合并进去（极少情况需要）
    for (const [k, v] of data.entries()) {
      formData.append(parentKey ? `${parentKey}[${k}]` : k, v)
    }
    return
  }

  if (isDate || isFile || typeof data !== 'object') {
    formData.append(parentKey, isDate ? data.toISOString() : data)
    return
  }

  if (Array.isArray(data)) {
    data.forEach((item) => {
      buildFormData(formData, item, `${parentKey}[]`)
    })
    return
  }

  // 普通对象
  Object.keys(data).forEach((key) => {
    const value = data[key]
    const newKey = parentKey ? `${parentKey}[${key}]` : key
    buildFormData(formData, value, newKey)
  })
}

// 防抖：避免多请求同时触发重复跳转
let isRedirectingToLogin = false
const toLoginSafely = () => {
  if (isRedirectingToLogin) return
  isRedirectingToLogin = true
  try {
    // 清空本地登录状态，避免路由守卫误判为已登录导致来回跳转
    localStorage.removeItem('userInfo')
    localStorage.removeItem('token')
    sessionStorage.removeItem('userInfo')
  } catch (_) {}

  const atLogin = window.location.hash === '#/login'
  if (!atLogin) {
    // 保持当前应用所在路径，仅替换 hash，避免部署在子目录时跳错到站点根路径
    const targetUrl = `${window.location.pathname}${window.location.search}#/login`
    window.location.replace(targetUrl)
  } else {
    // 已经在登录页则不再刷新
    isRedirectingToLogin = false
  }
}

// 请求拦截器：统一附加 Authorization，并将 POST 的 data 转为 form-data
service.interceptors.request.use(
  (req) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    const url = req?.url || ''
    // 除登录接口外自动携带 token；与现有用法一致，不加 Bearer 前缀
    if (token && !/\/user\/login/i.test(url)) {
      req.headers = req.headers || {}
      req.headers.Authorization = token
    }

    // 将所有 POST 请求的 data 自动转换为 FormData（若已经是 FormData 则跳过）
    const method = (req.method || '').toLowerCase()
    if (method === 'post' && req.data && !(req.data instanceof FormData)) {
      const fd = new FormData()
      // 仅当传入是对象时才转换，其他类型直接包装
      if (typeof req.data === 'object' && !Array.isArray(req.data)) {
        Object.keys(req.data).forEach((k) => buildFormData(fd, req.data[k], k))
      } else {
        buildFormData(fd, req.data, 'data')
      }
      req.data = fd
      // 切记不要手动设置 Content-Type，浏览器会自动带上 boundary
      if (req.headers && req.headers['Content-Type']) {
        delete req.headers['Content-Type']
      }
    }

    return req
  },
  (error) => Promise.reject(error)
)

// 响应拦截器：统一处理后端约定的返回格式
service.interceptors.response.use(
  (response) => {
    const res = response.data
    // 业务状态码判断
    if (res && typeof res.status !== 'undefined') {
      if (res.status === 200) {
        return res.data // 直接返回 data 以便调用方使用
      } else {
        // 非 200：统一错误提示；鉴权失效时跳转登录
        const url = response?.config?.url || ''
        const isLoginApi = /\/user\/login/i.test(url)
        const raw = res.data
        const msg = typeof raw === 'string'
          ? raw
          : (res.message || (raw == null ? '请求失败' : JSON.stringify(raw)))
        if (!isLoginApi && Number(res.status) === 402) {
          toLoginSafely()
        }
        ElMessage.error(msg || '请求失败')
        return Promise.reject(new Error(msg))
      }
    }
    // 不符合约定格式，直接返回
    return res
  },
  (error) => {
    // HTTP 层面的错误（如 401/403/500 等）
    const bizStatus = Number(error?.response?.data?.status || 0)
    const httpStatus = Number(error?.response?.status || 0)
    const status = bizStatus || httpStatus
    const url = error?.config?.url || error?.response?.config?.url || ''
    const isLoginApi = /\/user\/login/i.test(url)
    const raw = error?.response?.data
    const msg = typeof raw === 'string'
      ? raw
      : (raw?.data || raw?.message || error?.message || '网络异常')

    ElMessage.error(msg || '网络异常')

    // token 失效（后端业务码 402 / HTTP 401）时统一跳转登录
    if (!isLoginApi && (status === 402 || status === 401)) {
      toLoginSafely()
    }
    return Promise.reject(error)
  }
)

// 流式请求封装
const streamRequest = async (url, data = {}, options = {}) => {
  try {
    const formData = new FormData()
    
    // 将数据转换为FormData
    Object.keys(data).forEach(key => {
      formData.append(key, data[key])
    })
    
    // 获取token
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    
    // 构建请求头
    const headers = {
      'Accept': 'text/event-stream',
      ...options.headers
    }
    
    // 添加token（除登录接口外）
    if (token && !/\/user\/login/i.test(url)) {
      headers.Authorization = token
    }
    
    // 构建完整URL
    const fullUrl = `${config.baseURL}${url}`
    
    // 创建AbortController用于超时控制（支持自定义超时）
    const controller = new AbortController()
    const timeoutMs = (options && typeof options.timeout === 'number') ? options.timeout : (config.timeout || 1800000)
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, timeoutMs)
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      body: formData,
      credentials: 'omit', // 不携带cookies，避免CORS错误
      headers,
      signal: controller.signal, // 添加超时控制
      ...options
    })
    
    // 清除超时定时器
    clearTimeout(timeoutId)

    const contentType = String(response.headers.get('content-type') || '').toLowerCase()

    // 非 SSE 请求若直接返回业务 JSON，需要在这里补做一次统一鉴权处理
    if (response.ok && contentType.includes('application/json')) {
      const clone = response.clone()
      let parsed = null
      try {
        parsed = await clone.json()
      } catch (_) {
        parsed = null
      }
      const bizStatus = Number(parsed?.status || 0)
      const raw = parsed?.data
      const msg = typeof raw === 'string'
        ? raw
        : (parsed?.message || (raw == null ? '请求失败' : JSON.stringify(raw)))
      if (!/\/user\/login/i.test(url) && bizStatus === 402) {
        toLoginSafely()
      }
      if (bizStatus && bizStatus !== 200) {
        if (!options.silent) {
          ElMessage.error(msg || '请求失败')
        }
        throw new Error(msg || '请求失败')
      }
    }
    
    // 处理HTTP错误
    if (!response.ok) {
      const errorData = await response.text()
      let errorMsg = '网络异常'
      let parsed = null
      
      try {
        parsed = JSON.parse(errorData)
        errorMsg = parsed.data || parsed.message || errorMsg
      } catch {
        errorMsg = errorData || errorMsg
      }
      
      // token失效处理：HTTP 401 或业务码 402
      const bizStatus = Number(parsed?.status || 0)
      if (!/\/user\/login/i.test(url) && (response.status === 401 || bizStatus === 402)) {
        toLoginSafely()
      }

      if (!options.silent) {
        ElMessage.error(errorMsg)
      }
      throw new Error(errorMsg)
    }
    
    return response
  } catch (error) {
    // 处理超时错误
    if (error.name === 'AbortError') {
      const timeoutError = new Error('请求超时，请检查网络连接')
      timeoutError.isTimeout = true
      if (!options.silent) {
        ElMessage.error('请求超时，请检查网络连接')
      }
      throw timeoutError
    }
    
    // 处理网络连接错误（包含浏览器 fetch/HTTP2 协议中断常见 TypeError）
    const errorMsg = String(error?.message || '')
    if (
      error?.name === 'TypeError' ||
      errorMsg.includes('Failed to fetch') ||
      errorMsg.includes('NetworkError') ||
      errorMsg.includes('ERR_HTTP2_PROTOCOL_ERROR') ||
      errorMsg.includes('Load failed') ||
      errorMsg.includes('terminated')
    ) {
      const networkError = new Error('网络连接失败，请检查网络状态')
      networkError.isNetworkError = true
      networkError.rawMessage = errorMsg
      if (!options.silent) {
        ElMessage.error('网络连接失败，请检查网络状态')
      }
      throw networkError
    }
    
    // 统一错误处理
    if (error.message !== '网络异常' && !error.message.includes('HTTP error')) {
      if (!options.silent) {
        ElMessage.error(error.message || '请求失败')
      }
    }
    throw error
  }
}

// 导出service和streamRequest
service.streamRequest = streamRequest

export default service
